import type { Engine } from '../engine/Engine';
import { center, rectOf, union } from '../engine/geometry';
import type { Item, Point, Rect } from '../engine/types';

export type SaveMode = 'board' | 'selection';

/** Items a template saves: the whole board, or the selection with everything inside selected frames. */
export function templateSnapshot(engine: Engine, mode: SaveMode): Item[] {
  const items = mode === 'selection' ? engine.expandedSelection() : [...engine.doc.all()];
  return items.map((it) => {
    const copy = structuredClone(it);
    delete copy.locked;
    return copy;
  });
}

/** Extent of a snapshot; connectors count by their ends (their own box may be empty). */
export function itemsBounds(items: Item[]): Rect | null {
  return union(
    items.flatMap((it) =>
      it.type === 'connector'
        ? [
            { x: it.from.x, y: it.from.y, w: 0, h: 0 },
            { x: it.to.x, y: it.to.y, w: 0, h: 0 },
          ]
        : [rectOf(it)],
    ),
  );
}

/**
 * Preview for the gallery, rendered by the board engine like the dashboard thumbnail: 640x400,
 * transparent (the card draws its own background in either theme), only the template's items.
 */
export function templatePreview(engine: Engine, items: Item[]): Promise<Blob | null> {
  const ids = new Set(items.map((i) => i.id));
  const bounds = union(items.filter((i) => i.type !== 'connector').map((i) => (i.type === 'frame' ? { x: i.x, y: i.y - 40, w: i.w, h: i.h + 40 } : rectOf(i)))) ?? itemsBounds(items);
  if (!bounds || !engine.renderer.ready) return Promise.resolve(null);
  const w = 640, h = 400;
  let r = { ...bounds, w: Math.max(bounds.w, 1), h: Math.max(bounds.h, 1) };
  if (r.w / r.h > w / h) {
    const nh = (r.w * h) / w;
    r = { ...r, y: r.y - (nh - r.h) / 2, h: nh };
  } else {
    const nw = (r.h * w) / h;
    r = { ...r, x: r.x - (nw - r.w) / 2, w: nw };
  }
  const pad = r.w * 0.06;
  r = { x: r.x - pad, y: r.y - pad * (h / w), w: r.w + pad * 2, h: r.h + pad * 2 * (h / w) };
  const canvas = engine.renderer.capture(r, w, h, null, (q) => engine.spatial.search(q).filter((id) => ids.has(id)), true);
  engine.requestRender();
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.82));
}

/**
 * Copies template items onto the board with fresh ids (connectors rewired), centered on `at`
 * (the viewport center by default), brings them into view and selects them unless told not to.
 */
export function placeTemplate(engine: Engine, items: Item[], opts: { at?: Point; select?: boolean } = {}): string[] {
  if (engine.readOnly || items.length === 0) return [];
  const b = itemsBounds(items);
  const target = opts.at ?? engine.viewportCenter();
  const c = b ? center(b) : target;
  // Never on top of what is already on the board: the nearest free spot instead.
  const d = b ? engine.freeSpot({ ...b, x: b.x + target.x - c.x, y: b.y + target.y - c.y }) : { x: 0, y: 0 };
  const { items: copies } = engine.cloneItems(items, target.x - c.x + d.x, target.y - c.y + d.y);
  engine.add(copies);
  const placed = itemsBounds(copies);
  if (placed) engine.zoomToRect(placed, true, 60, 1);
  engine.select(opts.select === false ? [] : copies.filter((i) => i.type !== 'connector').map((i) => i.id));
  return copies.map((i) => i.id);
}
